import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Generic multi-section PDF export.
 * @param {Object} opts
 * @param {HTMLElement} container root element containing sections
 * @param {string[]} sectionSelectors ordered selectors to capture
 * @param {string} title document title header
 * @param {string} periodLabel secondary period label
 * @param {function=} beforeCapture optional hook(el) to mutate DOM per section
 * @param {function=} afterCapture optional hook(el) to revert DOM per section
 * @param {boolean=} forcePageBreakPerSection when true, every section starts on a fresh
 *   page instead of being packed onto the current one whenever it happens to fit. Off by
 *   default, so callers that want the packing behaviour keep it.
 */
/**
 * Finds the best horizontal break point (in canvas pixels) near or before maxTargetY
 * so that no DOM element (table row, card, heading, etc.) is sliced in half.
 */
function findBestBreakY(el, canvas, startY, maxTargetY) {
  if (maxTargetY >= canvas.height) {
    return canvas.height;
  }

  if (!el || typeof el.getBoundingClientRect !== 'function') {
    return maxTargetY;
  }

  const elRect = el.getBoundingClientRect();
  if (!elRect.width || elRect.width <= 0) {
    return maxTargetY;
  }

  const scale = canvas.width / elRect.width;

  // Query content block elements that shouldn't be sliced across pages
  const contentElements = el.querySelectorAll(
    'tr, .seom-cat-row, .seom-item-row, .seom-quadrant, .seom-box, .seom-bar-row, .seom-vis-group, .seom-actions li, ul.seom-actions > li, li, .seom-read, .seom-footnote, .seom-kpis, .seom-lede, .eeom-table tr, .eeom-kpis div, .eeom-callout, .eeom-lede, .seom-recon, .seom-caveat, .seom-verdict, .weom-box, .weom-read, .weom-quadrant-block, p'
  );

  // Query headings: break BEFORE them so headings are never orphaned at the bottom of a page
  const headingElements = el.querySelectorAll(
    'h1, h2, h3, h4, .seom-subhead, .eeom-subhead, .weom-subhead, .seom-eyebrow, .eeom-eyebrow, .weom-eyebrow'
  );

  const candidateBoundaries = [];
  contentElements.forEach((item) => {
    const rect = item.getBoundingClientRect();
    const bottomPx = Math.round((rect.bottom - elRect.top) * scale);
    if (bottomPx > startY && bottomPx <= maxTargetY) {
      candidateBoundaries.push(bottomPx);
    }
  });

  headingElements.forEach((item) => {
    const rect = item.getBoundingClientRect();
    const topPx = Math.round((rect.top - elRect.top) * scale);
    if (topPx > startY && topPx <= maxTargetY) {
      candidateBoundaries.push(topPx);
    }
  });

  if (candidateBoundaries.length > 0) {
    // Sort descending to find the furthest row/heading boundary that still fits before maxTargetY
    candidateBoundaries.sort((a, b) => b - a);
    const minAcceptableY = startY + (maxTargetY - startY) * 0.4;
    for (const b of candidateBoundaries) {
      if (b >= minAcceptableY) {
        return Math.min(b, canvas.height);
      }
    }
  }

  // Fallback: look for horizontal whitespace/background row in canvas
  try {
    const ctx = canvas.getContext('2d');
    if (ctx && typeof ctx.getImageData === 'function') {
      const width = canvas.width;
      const minScanY = Math.max(0, Math.floor(startY + (maxTargetY - startY) * 0.7));
      const scanHeight = Math.floor(maxTargetY - minScanY);
      if (scanHeight > 0) {
        const imgData = ctx.getImageData(0, minScanY, width, scanHeight).data;
        for (let relY = scanHeight - 1; relY >= 0; relY--) {
          const rowStart = relY * width * 4;
          let isWhite = true;
          for (let x = 0; x < width * 4; x += 16) {
            const r = imgData[rowStart + x];
            const g = imgData[rowStart + x + 1];
            const b = imgData[rowStart + x + 2];
            const a = imgData[rowStart + x + 3];
            if (a > 20 && (r < 240 || g < 240 || b < 240)) {
              isWhite = false;
              break;
            }
          }
          if (isWhite) {
            return minScanY + relY;
          }
        }
      }
    }
  } catch (e) {
    // Canvas security/read errors in some contexts
  }

  return maxTargetY;
}

export async function exportSectionsToPDF(opts) {
  const {
    container,
    sectionSelectors,
    title = 'Report',
    periodLabel = '',
    beforeCapture,
    afterCapture,
    hideSelectorsDuringCapture,
    forcePageBreakPerSection = false,
    fullPageMode = false,
    trimWhitePadding = !fullPageMode,
    suppressOverlayHeaderFooter = fullPageMode,
  } = opts;
  if (!container) throw new Error('Missing container');
  const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = fullPageMode ? 0 : 10;
  const headerHeight = (fullPageMode || suppressOverlayHeaderFooter) ? 0 : 8;
  const contentWidth = fullPageMode ? pageWidth : pageWidth - margin * 2;
  const usableHeight = fullPageMode ? pageHeight : pageHeight - margin - (margin + headerHeight);

  const addHeaderFooter = (pageNo) => {
    if (suppressOverlayHeaderFooter) return;
    pdf.setFontSize(12);
    pdf.text(title, margin, margin + 5);
    pdf.setFontSize(9);
    if (periodLabel) pdf.text(`Period: ${periodLabel}`, pageWidth - margin, margin + 5, { align: 'right' });
    pdf.text(`Page ${pageNo}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
  };

  const pxToMm = (px, canvasWidthPx) => (px * contentWidth) / canvasWidthPx;
  const startY = margin + headerHeight;
  let currentY = startY;
  let currentPage = 1;
  if (!suppressOverlayHeaderFooter) {
    addHeaderFooter(currentPage);
  }
  const sectionSpacing = fullPageMode ? 0 : 4;
  const newPage = () => {
    pdf.addPage();
    currentPage++;
    if (!suppressOverlayHeaderFooter) {
      addHeaderFooter(currentPage);
    }
    currentY = startY;
  };

  // Trim pure white rows top/bottom with tolerance.
  const trimCanvas = (cv) => {
    const ctx = cv.getContext('2d');
    const { width, height } = cv;
    const imgData = ctx.getImageData(0, 0, width, height).data;
    const isRowWhite = (y) => {
      const rowStart = y * width * 4;
      const rowEnd = rowStart + width * 4;
      for (let i = rowStart; i < rowEnd; i += 4) {
        const r = imgData[i], g = imgData[i+1], b = imgData[i+2], a = imgData[i+3];
        if (a > 10 && (r < 250 || g < 250 || b < 250)) return false;
      }
      return true;
    };
    let top = 0, bottom = height - 1;
    while (top < height && isRowWhite(top)) top++;
    while (bottom > top && isRowWhite(bottom)) bottom--;
    const newH = bottom - top + 1;
    if (newH >= height || newH <= 0) return cv;
    const trimmed = document.createElement('canvas');
    trimmed.width = width; trimmed.height = newH;
    trimmed.getContext('2d').drawImage(cv, 0, top, width, newH, 0, 0, width, newH);
    return trimmed;
  };

  let pagesDrawn = 0;

  for (const selector of sectionSelectors) {
    const el = container.querySelector(selector);
    if (!el) continue;

    // Full page mode: each section is its own A4 page.
    if (fullPageMode) {
      if (pagesDrawn > 0) {
        pdf.addPage();
        currentPage++;
      }

      let hiddenNodes = [];
      if (hideSelectorsDuringCapture && Array.isArray(hideSelectorsDuringCapture)) {
        hideSelectorsDuringCapture.forEach(sel => {
          el.querySelectorAll(sel).forEach(node => {
            hiddenNodes.push({ node, original: node.style.display });
            node.style.display = 'none';
          });
        });
      }
      if (beforeCapture) await beforeCapture(el);
      let canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      if (afterCapture) afterCapture(el);
      hiddenNodes.forEach(({ node, original }) => { node.style.display = original || ''; });

      if (trimWhitePadding) {
        canvas = trimCanvas(canvas);
      }

      const fullHeightMm = (canvas.height * pageWidth) / canvas.width;
      if (fullHeightMm <= pageHeight + 2) {
        // Fits comfortably on one A4 page
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, pageWidth, Math.min(fullHeightMm, pageHeight), undefined, 'FAST');
      } else {
        // Multi-page overflow with smart row-boundary breaking and preserved header/footer
        const elRect = (typeof el.getBoundingClientRect === 'function') ? el.getBoundingClientRect() : { top: 0, width: el.offsetWidth || 960 };
        const scale = elRect.width > 0 ? (canvas.width / elRect.width) : 2;
        const pageHeightPx = Math.floor((pageHeight * canvas.width) / pageWidth);
        const topPaddingPx = Math.round(36 * (scale / 2));
        const bottomPaddingPx = Math.round(48 * (scale / 2));

        // Locate footer
        let footerCanvas = null;
        let footerHeightPx = 0;
        const footerEl = el.querySelector ? el.querySelector('.seom-footer, .eeom-footer, .weom-footer, .lkm-doc-footer') : null;
        if (footerEl && typeof footerEl.getBoundingClientRect === 'function' && typeof document !== 'undefined') {
          const fRect = footerEl.getBoundingClientRect();
          const fTopPx = Math.round((fRect.top - elRect.top - 16) * scale);
          footerHeightPx = canvas.height - fTopPx;
          if (footerHeightPx > 0 && fTopPx > 0 && typeof document.createElement === 'function') {
            footerCanvas = document.createElement('canvas');
            footerCanvas.width = canvas.width;
            footerCanvas.height = footerHeightPx;
            const fCtx = footerCanvas.getContext('2d');
            if (fCtx) {
              fCtx.fillStyle = '#ffffff';
              fCtx.fillRect(0, 0, canvas.width, footerHeightPx);
              fCtx.drawImage(canvas, 0, fTopPx, canvas.width, footerHeightPx, 0, 0, canvas.width, footerHeightPx);
            }
          }
        }

        let y = 0;
        let sliceIdx = 0;
        while (y < canvas.height) {
          if (sliceIdx > 0) {
            pdf.addPage();
            currentPage++;
          }

          let sliceTopOffset = 0;
          let maxAvailHeight = pageHeightPx - bottomPaddingPx;

          if (sliceIdx === 0) {
            // Page 1: content starts at y = 0 (top padding already included in canvas)
            sliceTopOffset = 0;
            maxAvailHeight = pageHeightPx - (footerCanvas ? footerHeightPx : bottomPaddingPx);
          } else {
            // Page 2+: content offset by top padding
            sliceTopOffset = topPaddingPx;
            maxAvailHeight = pageHeightPx - sliceTopOffset - (footerCanvas ? footerHeightPx : bottomPaddingPx);
          }

          const targetMaxY = Math.min(y + maxAvailHeight, canvas.height);
          const breakY = (targetMaxY >= canvas.height) ? canvas.height : findBestBreakY(el, canvas, y, targetMaxY);
          const chunkHeightPx = breakY - y;

          if (chunkHeightPx <= 0) {
            break;
          }

          let pageCanvas = canvas;
          if (typeof document !== 'undefined' && typeof document.createElement === 'function') {
            const created = document.createElement('canvas');
            created.width = canvas.width;
            created.height = pageHeightPx;
            const pCtx = created.getContext ? created.getContext('2d') : null;
            if (pCtx) {
              pCtx.fillStyle = '#ffffff';
              pCtx.fillRect(0, 0, canvas.width, pageHeightPx);

              // Draw chunk content
              pCtx.drawImage(canvas, 0, y, canvas.width, chunkHeightPx, 0, sliceTopOffset, canvas.width, chunkHeightPx);

              // Draw footer
              if (footerCanvas && (breakY < canvas.height - footerHeightPx || sliceIdx > 0)) {
                pCtx.drawImage(footerCanvas, 0, pageHeightPx - footerHeightPx);
              }
              pageCanvas = created;
            }
          }

          pdf.addImage(pageCanvas.toDataURL('image/png'), 'PNG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');
          y = breakY;
          sliceIdx++;
        }
      }
      pagesDrawn++;
      continue;
    }

    // Standard / legacy packed layout mode
    if (forcePageBreakPerSection && currentY !== startY) newPage();
    let hiddenNodes = [];
    if (hideSelectorsDuringCapture && Array.isArray(hideSelectorsDuringCapture)) {
      hideSelectorsDuringCapture.forEach(sel => {
        el.querySelectorAll(sel).forEach(node => {
          hiddenNodes.push({ node, original: node.style.display });
          node.style.display = 'none';
        });
      });
    }
    if (beforeCapture) await beforeCapture(el);
    let canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
    if (afterCapture) afterCapture(el);
    hiddenNodes.forEach(({ node, original }) => { node.style.display = original || ''; });
    if (trimWhitePadding) {
      canvas = trimCanvas(canvas);
    }
    const fullHeightMm = pxToMm(canvas.height, canvas.width);
    if (fullHeightMm <= (pageHeight - margin - currentY)) {
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, currentY, contentWidth, fullHeightMm, undefined, 'FAST');
      currentY += fullHeightMm + sectionSpacing;
      pagesDrawn++;
      continue;
    }
    if (fullHeightMm <= usableHeight) {
      if (currentY !== startY) newPage();
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, currentY, contentWidth, fullHeightMm, undefined, 'FAST');
      currentY += fullHeightMm + sectionSpacing;
      pagesDrawn++;
      continue;
    }
    const sliceHeightPx = Math.floor((usableHeight * canvas.width) / contentWidth);
    let y = 0;
    while (y < canvas.height) {
      const remainingPx = canvas.height - y;
      const nextSlicePx = Math.min(sliceHeightPx, remainingPx);
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = canvas.width; sliceCanvas.height = nextSlicePx;
      sliceCanvas.getContext('2d').drawImage(canvas, 0, y, canvas.width, nextSlicePx, 0, 0, canvas.width, nextSlicePx);
      const sliceMm = pxToMm(nextSlicePx, canvas.width);
      if (currentY !== startY) newPage();
      pdf.addImage(sliceCanvas.toDataURL('image/png'), 'PNG', margin, currentY, contentWidth, sliceMm, undefined, 'FAST');
      y += nextSlicePx;
      if (y < canvas.height) newPage(); else currentY += sliceMm + sectionSpacing;
    }
    pagesDrawn++;
  }
  const ts = new Date().toISOString().replace(/[:T]/g,'-').split('.')[0];
  pdf.save(`${title.replace(/\s+/g,'_')}_${ts}.pdf`);
}
